import { Outlet, useLocation } from 'react-router-dom';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { log } from '@app/shared/utils/logger';
import { MainSection } from '@app/shared/ui/atoms/Layout';
import { Header, Footer } from '@app/shared/ui/organisms/Layout';
import { UserAgreementModal, DailyRewardModal, Modal } from '@app/shared/ui/organisms/Common';
import { useDailyRewardModal } from '@app/shared/hooks/useDailyRewardModal';
import { useSyncUserLanguage } from '@app/shared/hooks/useSyncUserLanguage';
import {
  showHeader,
  showFooter,
  includePadding,
  showUserAgreementModal,
} from '@app/shared/utils/layout';
import { cn } from '@app/shared/utils/common';
import { eatingPandaImage, pandaHand, settlementGift } from '@app/assets';
import { PredictionCookieReveal } from '@app/shared/ui/molecules/Common';
import { Page } from '@app/routes';
import notification from '@app/shared/utils/notification';
import { LOCAL_STORAGE } from '@app/shared/constants/common';
import {
  useGetCurrentUserProfileQuery,
  useReceiveGiftMutation,
  usersApi,
  useUpdateCurrentUserProfileMutation,
} from '@app/store/slices/users/usersApi';
import { Button, Paragraph, Image } from '@app/shared/ui/atoms/Common';
import { useTutorialPersist } from '@app/shared/hooks/useTutorialPersist';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { closeModal } from '@app/store/slices/modal/modalSlice';
import { selectIsAuthenticated } from '@app/store/selectors';

export function AuthenticatedLayout() {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const activeModal = useAppSelector((state) => state.modal.activeModal);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const authState = useAppSelector((state) => state.auth);

  useSyncUserLanguage();
  useTutorialPersist();

  const hasHeader = showHeader(pathname);
  const hasFooter = showFooter(pathname);
  const hasPadding = includePadding(pathname);

  const { data: userProfile, isLoading: isLoadingProfile } = useGetCurrentUserProfileQuery();
  const [updateCurrentUserProfile] = useUpdateCurrentUserProfileMutation();

  useEffect(() => {
    void log({
      level: 'info',
      message: 'authLayoutState',
      extra: {
        pathname,
        isAuthenticated,
        isBootstrapping: authState.isBootstrapping,
        hasAccessToken: !!authState.accessToken,
        isLoadingProfile,
        hasUserProfile: !!userProfile,
        activeModal,
      },
    });
  }, [
    activeModal,
    authState.accessToken,
    authState.isBootstrapping,
    isAuthenticated,
    isLoadingProfile,
    pathname,
    userProfile,
  ]);

  const onClickUserAgreement = async () => {
    try {
      if (!userProfile?.isUserAgreementRead) {
        await updateCurrentUserProfile({
          isUserAgreementRead: true,
        }).unwrap();
        dispatch(
          usersApi.util.updateQueryData('getCurrentUserProfile', undefined, (draft) => {
            if (draft) {
              draft.isUserAgreementRead = true;
            }
          })
        );
      }

      dispatch(closeModal());
    } catch (error: unknown) {
      void log({ level: 'error', message: 'onClickUserAgreement error', error });
    }
  };

  const hasUserAgreementModal = showUserAgreementModal(pathname);

  const {
    handleDailyRewardAccept,
    flyingCookie,
    dailyRewardStatus,
    isClaimingDailyReward,
    isPredictionModalOpen,
    setIsPredictionModalOpen,
    isCloseWindow,
  } = useDailyRewardModal();

  const [giftToken, setGiftToken] = useLocalStorage<string | null>(LOCAL_STORAGE.gift, null);
  const [isGiftAvailable, setIsGiftAvailable] = useState(false);
  const [receiveGift, { isLoading: isReceivingGift }] = useReceiveGiftMutation();

  const hasBlockingModal =
    (hasUserAgreementModal &&
      !isLoadingProfile &&
      userProfile &&
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      !userProfile.isUserAgreementRead) ||
    activeModal === 'userAgreement' ||
    (dailyRewardStatus &&
      (pathname as Page) !== Page.USER_DATA_FORM &&
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      !dailyRewardStatus.claimedToday) ||
    isPredictionModalOpen;

  useEffect(() => {
    if (!giftToken) {
      return;
    }
    if (hasBlockingModal) {
      return;
    }

    const run = async () => {
      try {
        await receiveGift(giftToken).unwrap();
        setIsGiftAvailable(true);
      } catch (err: unknown) {
        if (
          typeof err === 'object' &&
          err &&
          'data' in err &&
          'status' in err &&
          typeof err.data === 'object' &&
          err.data &&
          'message' in err.data &&
          typeof err.data.message === 'string'
        ) {
          if (err.data.message === 'GIFT_NOT_FOUND') {
            notification.error(t('errors.gift_not_found'));
          } else if (err.data.message === 'SETTLEMENT_ALREADY_PURCHASED_GIFT') {
            notification.error(t('errors.settlement_already_purchased'));
          }
        }

        setGiftToken(null);
      }
    };

    void run();
  }, [giftToken, hasBlockingModal, receiveGift, setGiftToken]);

  const handleCloseGiftModal = () => {
    setIsGiftAvailable(false);
    setGiftToken(null);
  };

  return (
    <>
      {hasHeader && <Header />}

      <MainSection className={cn(hasPadding && 'px-5')}>
        <Outlet />
      </MainSection>

      {hasFooter && <Footer />}

      <UserAgreementModal
        isOpen={
          activeModal === 'userAgreement' ||
          (!isLoadingProfile && !!userProfile && !userProfile.isUserAgreementRead)
        }
        onContinue={onClickUserAgreement}
      />

      {dailyRewardStatus && (pathname as Page) !== Page.USER_DATA_FORM && (
        <DailyRewardModal
          isOpen={!isLoadingProfile && !dailyRewardStatus.claimedToday && !isCloseWindow}
          streak={dailyRewardStatus.currentStreak + 1}
          isClaimingReward={isClaimingDailyReward}
          flyingCookie={flyingCookie}
          onRewardAccept={handleDailyRewardAccept}
        />
      )}

      {dailyRewardStatus?.cookie && (
        <Modal
          isOpen={isPredictionModalOpen}
          onOpenChange={setIsPredictionModalOpen}
          featuredImg={<img src={eatingPandaImage} alt="eating panda" className="w-[134px]" />}
          hiddenTitle="Feed panda"
        >
          <PredictionCookieReveal cookie={dailyRewardStatus.cookie} />
        </Modal>
      )}

      <Modal
        isOpen={isGiftAvailable}
        onOpenChange={handleCloseGiftModal}
        contentClassName="pt-20 shadow-blue"
        hiddenTitle="Gift received"
        featuredImg={
          <div className="relative -top-[23%]">
            <Image src={pandaHand} className="size-[100px] absolute -left-[35%] top-[20%] z-10" />
            <Image src={settlementGift} />
          </div>
        }
      >
        <Paragraph>
          {t('gift_modal.receive_earth_text', { settlement: t('settlements.titles.land') })}
        </Paragraph>
        <Button disabled={isReceivingGift} onClick={handleCloseGiftModal}>
          {isReceivingGift ? t('common.wait') : t('common.get')}
        </Button>
      </Modal>
    </>
  );
}
